import * as React from "react";
import { useRef } from "react";
import { Combobox, Option } from "@fluentui/react-components";
import { Add20Regular, ArrowCounterclockwise20Regular, Dismiss12Regular, Wand20Regular } from "@fluentui/react-icons";
import Button from "../Button";
import { FILTER_TYPE_LABELS, formatEntityTypeLabel, type EntityTypeItem, type FilterRow } from "../TemplateServices";
import { ActionIconButton, SectionHeader, type TemplateTabViewStyles } from "./TemplateTabViewStyles";
import type { FilterRowDefinition } from "./TemplateTabViewTypes";

type TemplateFiltersSectionProps = {
  styles: TemplateTabViewStyles;
  isConfigurationLocked: boolean;
  loadingFilters: boolean;
  entityNames: EntityTypeItem[];
  selectedFilters: FilterRow[];
  availableFilters: FilterRowDefinition[];
  showFilterPicker: boolean;
  setShowFilterPicker: (value: boolean) => void;
  setShowColumnPicker: (value: boolean) => void;
  handleAddFilter: (filterKey: string) => void;
  handleResetFilters: () => void;
  handleRemoveFilter: (key: string) => void;
  handleFilterDrop: (fromIndex: number, toIndex: number) => void;
  handleFilterTypeChange: (key: string, type: string) => void;
  handleFilterDisplayNameChange: (key: string, displayName: string) => void;
  handleFilterRequiredChange: (key: string, required: boolean) => void;
  handleFilterCascadeChange: (key: string, cascadeFrom: string | null) => void;
};

const TemplateFiltersSection: React.FC<TemplateFiltersSectionProps> = ({
  styles,
  isConfigurationLocked,
  loadingFilters,
  entityNames,
  selectedFilters,
  availableFilters,
  showFilterPicker,
  setShowFilterPicker,
  setShowColumnPicker,
  handleAddFilter,
  handleResetFilters,
  handleRemoveFilter,
  handleFilterDrop,
  handleFilterTypeChange,
  handleFilterDisplayNameChange,
  handleFilterRequiredChange,
  handleFilterCascadeChange,
}) => {
  const dragFilterRef = useRef<number | null>(null);
  const unselectedFilters = availableFilters.filter((filter) => !selectedFilters.find((row) => row.key === filter.key));
  const findEntityByCode = (value: string | undefined) => {
    if (!value) return undefined;
    const normalizedValue = value.trim().toLowerCase();
    return entityNames.find((entity) =>
      entity.key.toLowerCase() === normalizedValue ||
      entity.dataEndpoint.toLowerCase() === normalizedValue
    );
  };
  const formatFilterEntityLabel = (filter: Pick<FilterRowDefinition, "key" | "displayName" | "dropdownEndpoint">) => {
    const entity = findEntityByCode(filter.key) || findEntityByCode(filter.displayName) || findEntityByCode(filter.dropdownEndpoint);
    return entity ? formatEntityTypeLabel(entity) : filter.displayName;
  };
  const getCascadeOptions = (filterKey: string, cascadeParents: string[]) => {
    return selectedFilters.filter((row) => row.key !== filterKey && cascadeParents.includes(row.key));
  };

  return (
    <div className={styles.section}>
      <SectionHeader styles={styles} num={2} title="Configure filters (parameters)" />
      <div style={{ fontSize: "11px", color: "#6D6E71", padding: "6px 12px 8px 12px", borderBottom: "0.25pt solid #E8F0F0" }}>
        Add filters to allow users to narrow down the data.
      </div>
      <div className={styles.sectionBody}>
        <div className={styles.toolbarRow}>
          <Button
            icon={<Add20Regular style={{ fontSize: "14px" }} />}
            label="Add Filter"
            onClick={() => { setShowFilterPicker(!showFilterPicker); setShowColumnPicker(false); }}
            disabled={isConfigurationLocked || unselectedFilters.length === 0}
          />
          <Button
            icon={selectedFilters.length > 0
              ? <ArrowCounterclockwise20Regular style={{ fontSize: "14px" }} />
              : <Wand20Regular style={{ fontSize: "14px" }} />}
            label={selectedFilters.length > 0 ? "Reset" : "Auto-suggest"}
            onClick={handleResetFilters}
            disabled={isConfigurationLocked || availableFilters.length === 0 || loadingFilters}
          />
        </div>

        {loadingFilters && (
          <div style={{ fontSize: "11px", color: "#666", padding: "8px 12px", fontStyle: "italic" }}>
            Loading filters...
          </div>
        )}

        {showFilterPicker && !isConfigurationLocked && unselectedFilters.length > 0 && (
          <Combobox
            autoComplete="off"
            placeholder="Pick a filter to add..."
            onOptionSelect={(_, data) => { if (data.optionValue) handleAddFilter(data.optionValue); }}
            listbox={{ style: { maxHeight: "200px", overflowY: "auto" } }}
            style={{ width: "220px", fontSize: "11px", marginBottom: "8px" }}
            positioning={{
              position: "below",
              align: "start",
              pinned: true,
              autoSize: "width",
            }}
          >
            {unselectedFilters.map((filter) => {
              const filterLabel = formatFilterEntityLabel(filter);
              return (
                <Option key={filter.key} value={filter.key} text={filterLabel}>
                  {filterLabel}
                </Option>
              );
            })}
          </Combobox>
        )}

        {selectedFilters.length > 0 ? (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead className={styles.tableHead}>
                <tr>
                  <th className={styles.th} style={{ width: "16px" }}></th>
                  <th className={styles.th} style={{ width: "24px" }}>Order</th>
                  <th className={styles.th}>Parameter (Display Name)</th>
                  <th className={styles.th} style={{ width: "80px" }}>Type</th>
                  <th className={styles.th} style={{ width: "54px", textAlign: "center" }}>Required</th>
                  <th className={styles.th} style={{ width: "84px" }}>Cascade From</th>
                  <th className={styles.th} style={{ width: "30px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedFilters.map((row, index) => {
                  const cascadeOptions = getCascadeOptions(row.key, row.cascadeParents);
                  return (
                    <tr
                      key={row.key}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => {
                        if (dragFilterRef.current !== null) {
                          handleFilterDrop(dragFilterRef.current, index);
                          dragFilterRef.current = null;
                        }
                      }}
                    >
                      <td className={styles.td}>
                        <span
                          className={styles.dragHandle}
                          title="Drag to reorder"
                          draggable={!isConfigurationLocked}
                          onDragStart={(event) => {
                            dragFilterRef.current = index;
                            event.dataTransfer.effectAllowed = "move";
                          }}
                        >
                          &#8942;&#8942;
                        </span>
                      </td>
                      <td className={styles.td} style={{ textAlign: "center", color: "#999", fontSize: "11px" }}>
                        {index + 1}
                      </td>
                      <td className={styles.td}>
                        <input
                          type="text"
                          className={styles.templateField}
                          value={row.displayName}
                          onChange={(event) => handleFilterDisplayNameChange(row.key, event.target.value)}
                          disabled={isConfigurationLocked}
                          style={{ width: "160px", padding: "6px 8px", minHeight: "32px" }}
                        />
                        <span className={styles.paramSubtext}>
                          {row.apiField}{row.displayField ? `, ${row.displayField}` : ""}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <select
                          className={styles.inlineSelect}
                          value={row.type}
                          onChange={(event) => handleFilterTypeChange(row.key, event.target.value)}
                          disabled={isConfigurationLocked || row.allowedTypes.length === 1}
                          style={{ width: "76px" }}
                        >
                          {row.allowedTypes.map((type) => (
                            <option key={type} value={type}>{FILTER_TYPE_LABELS[type]}</option>
                          ))}
                        </select>
                      </td>
                      <td className={styles.td} style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          className={styles.inlineCheckbox}
                          checked={row.required}
                          onChange={(event) => handleFilterRequiredChange(row.key, event.target.checked)}
                          disabled={isConfigurationLocked}
                          title="Required"
                        />
                      </td>
                      <td className={styles.td}>
                        <select
                          className={styles.inlineSelect}
                          value={row.cascadeFrom || ""}
                          onChange={(event) => handleFilterCascadeChange(row.key, event.target.value || null)}
                          disabled={isConfigurationLocked || cascadeOptions.length === 0}
                          style={{ width: "80px" }}
                        >
                          <option value="">(None)</option>
                          {cascadeOptions.map((option) => {
                            const optionLabel = formatFilterEntityLabel(option);
                            return <option key={option.key} value={option.key}>{optionLabel}</option>;
                          })}
                        </select>
                      </td>
                      <td className={styles.td}>
                        <div className={styles.actionCell}>
                          <ActionIconButton
                            icon={<Dismiss12Regular />}
                            onClick={() => handleRemoveFilter(row.key)}
                            disabled={isConfigurationLocked}
                            title="Remove"
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.emptyRow}>
            {loadingFilters
              ? "Loading filters..."
              : "No filters configured. Use Add Filter or Auto-suggest above."}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateFiltersSection;
